import Vue from 'vue';
// vendors
import VueRecaptcha from 'vue-recaptcha';

// form inputs
import FieldComponent from '@/components/form-input/field.component.vue';
import InputComponent from '@/components/form-input/input.component.vue';
import SelectComponent from '@/components/form-input/select.component.vue';
import CheckBoxGroupComponent from '@/components/form-input/checkbox-group.component.vue';
import RadioGroupComponent from '@/components/form-input/radio-group.component.vue';
import HtmlInputComponent from '@/components/form-input/html-input.component.vue';

import ConfirmContainer from '@/containers/confirm/confirm.vue';
import AlertContainer from '@/containers/alert/alert.vue';

import LogoComponent from '@/components/logo/logo.component.vue';
import LoadingComponent from '@/components/loading/loading.component.vue';
import LoadingLineComponent from '@/components/loading-line/loading-line.component.vue';
import NoteComponent from '@/components/note/note.component.vue';
import PaginationComponent from '@/components/pagination/pagination.component.vue';
import DatepickerComponent from '@/components/datepicker/datepicker.component.vue';
import DatepickerComponentFull from '@/components/datepicker/datepicker-full.component.vue';

import DateRangePickerComponent from '@/components/date-range-picker/date-range-picker.component.vue';
import DateRangePickerComponentFull from '@/components/date-range-picker/date-range-picker-full.component.vue';
import DateRangePickerComponentFullEx from '@/components/date-range-picker/date-range-picker-full-ex.component.vue';

import DateRangeComponent from '@/components/date-range/date-range.component.vue';
import DateRangeExComponent from '@/components/date-range/date-range-ex.component.vue';
import DateRangeWeeklyComponent from '@/components/date-range/date-range-weekly.component.vue';
import DateRangeWeeklyExComponent from '@/components/date-range/date-range-weekly-ex.component.vue';
import TableComponent from '@/components/table/table.component.vue';
import TdSelectableComponent from '@/components/table/td-selectable.component.vue';
import ThSortableComponent from '@/components/table/th-sortable.component.vue';
import TrEmptyComponent from '@/components/table/tr-empty.component.vue';
import ActionLinkComponent from '@/components/links/action-link.component.vue';
import PageHeaderComponent from '@/components/page-header/page-header.component.vue';
import TimezoneBlockComponent from '@/components/timezone-block/timezone-block.component.vue';
import ShortLinkComponent from '@/components/custom/short-link-component.vue';
import BonusPriceComponent from '@/components/custom/bonus-price-component.vue';
import IpAddressLinkComponent from '@/components/custom/ip-address-link-component.vue';
import SingleLineCriteria from '@/components/criteria/single-line-criteria.vue';
import AvpPaySchedule from '@/components/avp-pay-schedule/avp-pay-schedule.vue';

import {
    formatDate,
    formatDateWithTime,
    formatPrice,
    formatPeso,
    formatPercent,
    formatPercentWithSign,
    formatPeriod,
    formatDecimal
} from '@/utils';

import { insertSpaces } from '@/utils/format.utils';

import {
    AccountRole,
    translateAccountRole,
    translateStatus
} from '@/entities/accounts';
import {
    SiteStatus,
    translateSiteStatus
} from '@/entities/sites/site-status.enum';
import { SiteType, translateSiteType } from '@/entities/sites/site-type.enum';
import {IPeriod, translateEventType, EventType} from '@/entities';
import * as types from '@/store/types';
import {IAvpPeriod} from '@/entities/avp-periods/avp-period.interface';
import {translatePreferredDomainHidden} from '@/entities/preferred-domains/preferred-domain-hidden.enum';

export function registerEntities() {
    // vendors
    Vue.component('vue-recaptcha', VueRecaptcha);

    // containers
    Vue.component('app-confirm', ConfirmContainer);
    Vue.component('app-alert', AlertContainer);

    // components
    Vue.component('app-logo', LogoComponent);
    Vue.component('app-loading-line', LoadingLineComponent);
    Vue.component('app-loading', LoadingComponent);
    Vue.component('app-field', FieldComponent);
    Vue.component('app-input', InputComponent);
    Vue.component('app-select', SelectComponent);
    Vue.component('app-html-input', HtmlInputComponent);
    Vue.component('app-checkbox-group', CheckBoxGroupComponent);
    Vue.component('app-radio-group', RadioGroupComponent);
    Vue.component('app-datepicker', DatepickerComponent);
    Vue.component('app-datepicker-full', DatepickerComponentFull);
    Vue.component('app-date-range-picker', DateRangePickerComponent);
    Vue.component('app-date-range-picker-full', DateRangePickerComponentFull);
    Vue.component('app-date-range-picker-full-ex', DateRangePickerComponentFullEx);
    Vue.component('app-date-range', DateRangeComponent);
    Vue.component('app-date-range-ex', DateRangeExComponent);
    Vue.component('app-date-range-weekly', DateRangeWeeklyComponent);
    Vue.component('app-date-range-weekly-ex', DateRangeWeeklyExComponent);
    Vue.component('app-note', NoteComponent);
    Vue.component('app-pagination', PaginationComponent);
    Vue.component('app-table', TableComponent);
    Vue.component('app-td-selectable', TdSelectableComponent);
    Vue.component('app-th-sortable', ThSortableComponent);
    Vue.component('app-tr-empty', TrEmptyComponent);
    Vue.component('app-action-link', ActionLinkComponent);
    Vue.component('page-header', PageHeaderComponent);
    Vue.component('app-timezone-block', TimezoneBlockComponent);
    Vue.component('app-short-link', ShortLinkComponent);
    Vue.component('bonus-price', BonusPriceComponent);
    Vue.component('app-ip-address', IpAddressLinkComponent);
    Vue.component('app-single-line-criteria', SingleLineCriteria);
    Vue.component('app-avp-pay-schedule', AvpPaySchedule);

    // TODO use suffix like dateTimePipe
    Vue.filter('dateWithTime', (value: string) => {
        return formatDateWithTime(value);
    });
    Vue.filter('date', (value: string) => {
        return formatDate(value);
    });
    Vue.filter('price', (value: number) => {
        return formatPrice(value);
    });
    Vue.filter('peso', (value: number) => {
        return formatPeso(value);
    });
    Vue.filter('percent-sign', (value: number) => {
        return formatPercentWithSign(value);
    });

    Vue.filter('percent', (value: number) => {
        return formatPercent(value);
    });

    Vue.filter('decimal', (value: number) => {
        return formatDecimal(value);
    });

    Vue.filter('period', (period: IPeriod) => {
        return formatPeriod(period);
    });

    Vue.filter('spaces', (str: string) => {
        return insertSpaces(str);
    });

    Vue.filter(
        'accountStatus',
        (item: { active: boolean; hidden: boolean }) => {
            return translateStatus(item);
        }
    );

    Vue.filter('accountRole', (value: AccountRole) => {
        return translateAccountRole(value);
    });

    Vue.filter('eventType', (value: EventType) => {
        return translateEventType(value);
    });

    Vue.filter('siteStatus', (value: SiteStatus) => {
        return translateSiteStatus(value);
    });

    Vue.filter('siteType', (value: SiteType) => {
        return translateSiteType(value);
    });

    Vue.filter('preferredDomainHidden', (value: boolean) => {
        return translatePreferredDomainHidden(value);
    });


    Vue.filter('avpPeriod', (value: IAvpPeriod) => {
        if (value.index > 1) {
            return `${value.from}${value.to < 1000 ? ' - ' + value.to : '+'}`;
        }
        return 'NO';
    });

    // Vue.prototype.$companyPermissions = function() {
    //     return this.$store.getters[types.AUTH_HAS_COMPANY_PERMISSIONS];
    // };

    // Vue.prototype.$adminPermissions = function() {
    //     return this.$store.getters[types.AUTH_HAS_SUPERADMIN_PERMISSIONS];
    // };
}
